import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CONTROL, PRES, TYPE } from './chrome';
import { COPY, pageFormatLabel } from './presentationCopy';
import {
  createLibraryLabel,
  createLibrarySlide,
  deleteLibraryLabel,
  listLibraryLabels,
  listLibrarySlides,
  patchLibraryLabel,
  patchLibrarySlide,
} from './api';
import { PageFormat, PresentationLibraryLabel, PresentationLibrarySlide, resolvePageFormat } from './types';
import {
  filterLibrarySlidesByLabel,
  libraryPreviewSlide,
  librarySlideLabelNames,
  resolveLibrarySlideLabelIds,
  splitLibrarySlidesByArchive,
  toggleLibrarySlideLabel,
} from './librarySlides';
import { focusVisibleSx } from './ui/focus';
import { SlideThumbnail } from './ui';
import SettingsGearButton from './SettingsGearButton';

interface LibraryHomeProps {
  onBack: () => void;
  onOpen: (librarySlideId: string) => void;
  onOpenSettings?: () => void;
}

function ChipButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      size="small"
      variant={active ? 'contained' : 'outlined'}
      onClick={onClick}
      sx={{
        textTransform: 'none',
        boxShadow: 'none',
        bgcolor: active ? PRES.interactive : PRES.surface,
        color: active ? PRES.white : PRES.textPrimary,
        borderColor: PRES.borderSubtle,
      }}
    >
      {children}
    </Button>
  );
}

const LibraryHome: React.FC<LibraryHomeProps> = ({ onBack, onOpen, onOpenSettings }) => {
  const [items, setItems] = useState<PresentationLibrarySlide[]>([]);
  const [labels, setLabels] = useState<PresentationLibraryLabel[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [archiveTab, setArchiveTab] = useState<'active' | 'archived'>('active');
  const [labelFilter, setLabelFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [pageFormat, setPageFormat] = useState<PageFormat>('widescreen_16_9');
  const [createLabelIds, setCreateLabelIds] = useState<string[]>([]);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  const [labelDrafts, setLabelDrafts] = useState<Record<string, string>>({});
  const [newLabelName, setNewLabelName] = useState('');
  const [cardMenu, setCardMenu] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const [labelMenu, setLabelMenu] = useState<{ id: string; anchor: HTMLElement } | null>(null);

  const reload = async () => {
    try {
      const [nextItems, nextLabels] = await Promise.all([
        listLibrarySlides({ includeArchived: true }),
        listLibraryLabels(),
      ]);
      setItems(nextItems);
      setLabels(nextLabels);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.loadFailed);
    }
  };

  useEffect(() => { void reload(); }, []);

  useEffect(() => {
    if (!manageOpen) return;
    setLabelDrafts(Object.fromEntries(labels.map((label) => [label._id, label.name])));
    setNewLabelName('');
  }, [labels, manageOpen]);

  const visible = useMemo(() => {
    const { active, archived } = splitLibrarySlidesByArchive(items);
    const inTab = archiveTab === 'archived' ? archived : active;
    return filterLibrarySlidesByLabel(inTab, labelFilter, labels);
  }, [archiveTab, items, labelFilter, labels]);

  const emptyCopy = archiveTab === 'archived' ? COPY.archivedLibraryEmpty : COPY.slideLibraryEmpty;
  const menuSlide = items.find((item) => item._id === (labelMenu?.id || cardMenu?.id));

  const assignLabels = async (item: PresentationLibrarySlide, labelId: string) => {
    try {
      await patchLibrarySlide(item._id, { labelIds: toggleLibrarySlideLabel(item, labelId, labels) });
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.saveError);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: PRES.surfaceSunken, fontFamily: TYPE.ui }}>
      <Box sx={{
        height: 56,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        bgcolor: PRES.surface,
        borderBottom: `1px solid ${PRES.borderSubtle}`,
        flexShrink: 0,
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            textTransform: 'none',
            color: PRES.interactive,
            fontWeight: 650,
            ...focusVisibleSx,
          }}
        >
          {COPY.allPresentations}
        </Button>
        <Box sx={{ width: '1px', alignSelf: 'stretch', my: 1.5, bgcolor: PRES.borderStrong, flexShrink: 0 }} />
        <Typography sx={{ color: PRES.textPrimary, fontFamily: TYPE.ui, ...TYPE.title }}>{COPY.slideLibrary}</Typography>
        <Box sx={{ flex: 1 }} />
        {onOpenSettings && <SettingsGearButton onClick={onOpenSettings} />}
        <Button
          sx={{ textTransform: 'none' }}
          onClick={() => setManageOpen(true)}
        >
          {COPY.manageLibraryLabels}
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setName('');
            setPageFormat('widescreen_16_9');
            setCreateLabelIds([]);
            setCreateOpen(true);
          }}
          sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
        >
          {COPY.newLibrarySlide}
        </Button>
      </Box>
      <Box sx={{ p: 3, overflow: 'auto' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ mb: 2, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.body }}>
          {COPY.slideLibraryHelp}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, borderBottom: `1px solid ${PRES.borderSubtle}` }}>
          {([
            { id: 'active' as const, label: COPY.libraryActiveTab },
            { id: 'archived' as const, label: COPY.libraryArchivedTab },
          ]).map((tab) => {
            const selected = archiveTab === tab.id;
            return (
              <Box
                key={tab.id}
                component="button"
                type="button"
                onClick={() => setArchiveTab(tab.id)}
                sx={{
                  border: 0,
                  borderBottom: selected ? `2px solid ${PRES.interactive}` : '2px solid transparent',
                  bgcolor: 'transparent',
                  color: selected ? PRES.textPrimary : PRES.textSecondary,
                  fontFamily: TYPE.ui,
                  fontSize: 15,
                  lineHeight: 1.3,
                  fontWeight: selected ? 700 : 500,
                  px: 0.25,
                  py: 1.25,
                  mb: '-1px',
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <ChipButton active={!labelFilter} onClick={() => setLabelFilter('')}>
            {COPY.libraryGroupAll}
          </ChipButton>
          {labels.map((label) => (
            <ChipButton
              key={label._id}
              active={labelFilter === label._id}
              onClick={() => setLabelFilter(label._id)}
            >
              {label.name}
            </ChipButton>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
          {visible.map((item) => {
            const assigned = librarySlideLabelNames(item, labels);
            return (
              <Box
                key={item._id}
                sx={{
                  bgcolor: PRES.surface,
                  border: `1px solid ${PRES.borderSubtle}`,
                  borderRadius: `${CONTROL.radiusCard}px`,
                  overflow: 'hidden',
                  opacity: item.isActive === false ? 0.7 : 1,
                }}
              >
                <Box onClick={() => onOpen(item._id)} sx={{ cursor: 'pointer' }}>
                  <SlideThumbnail
                    slide={libraryPreviewSlide(item)}
                    pageFormat={item.pageFormat}
                  />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary }}>
                    {item.name}
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.caption }}>
                    {pageFormatLabel(item.pageFormat)}
                    {assigned.length ? ` · ${assigned.join(', ')}` : ''}
                    {item.isActive === false ? ` · ${COPY.archivedLibrarySlide}` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
                      onClick={() => onOpen(item._id)}
                    >
                      {COPY.openLibrarySlide}
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <IconButton
                      size="small"
                      title={COPY.libraryCardMenu}
                      aria-label={COPY.libraryCardMenu}
                      onClick={(event) => setCardMenu({ id: item._id, anchor: event.currentTarget })}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
        {!visible.length && (
          <Typography sx={{ color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.body }}>
            {labelFilter ? COPY.noLibrarySlideMatches : emptyCopy}
          </Typography>
        )}
      </Box>
      <Menu
        open={Boolean(cardMenu)}
        anchorEl={cardMenu?.anchor}
        onClose={() => setCardMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (!menuSlide) return;
            setRenameId(menuSlide._id);
            setRenameValue(menuSlide.name);
            setCardMenu(null);
          }}
        >
          {COPY.renameLibrarySlide}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!cardMenu) return;
            setLabelMenu({ id: cardMenu.id, anchor: cardMenu.anchor });
            setCardMenu(null);
          }}
        >
          {COPY.libraryLabels}
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (!menuSlide) return;
            if (menuSlide.isActive !== false && !window.confirm(COPY.confirmArchiveLibrarySlide)) {
              setCardMenu(null);
              return;
            }
            try {
              await patchLibrarySlide(menuSlide._id, { isActive: menuSlide.isActive === false });
              setCardMenu(null);
              await reload();
            } catch (err: any) {
              setError(err?.response?.data?.message || COPY.saveError);
              setCardMenu(null);
            }
          }}
        >
          {menuSlide?.isActive === false ? COPY.restoreLibrarySlide : COPY.archiveLibrarySlide}
        </MenuItem>
      </Menu>
      <Menu
        open={Boolean(labelMenu)}
        anchorEl={labelMenu?.anchor}
        onClose={() => setLabelMenu(null)}
      >
        {!labels.length && (
          <MenuItem disabled>{COPY.noLibraryLabels}</MenuItem>
        )}
        {menuSlide && labels.map((label) => (
          <MenuItem key={label._id} onClick={() => { void assignLabels(menuSlide, label._id); }}>
            <Checkbox
              size="small"
              checked={resolveLibrarySlideLabelIds(menuSlide, labels).includes(label._id)}
              sx={{ pl: 0 }}
            />
            {label.name}
          </MenuItem>
        ))}
      </Menu>
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{COPY.newLibrarySlide}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={COPY.librarySlideName}
            placeholder={COPY.librarySlideNamePlaceholder}
            value={name}
            onChange={(event) => setName(event.target.value)}
            sx={{ mt: 1 }}
          />
          <Select
            fullWidth
            size="small"
            value={pageFormat}
            onChange={(event) => setPageFormat(resolvePageFormat(event.target.value))}
            sx={{ mt: 2 }}
          >
            <MenuItem value="widescreen_16_9">{COPY.pageFormatWidescreen}</MenuItem>
            <MenuItem value="a4_portrait">{COPY.pageFormatA4}</MenuItem>
          </Select>
          <Typography sx={{ mt: 2, mb: 1, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.caption }}>
            {COPY.libraryLabels}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {labels.map((label) => (
              <ChipButton
                key={label._id}
                active={createLabelIds.includes(label._id)}
                onClick={() => setCreateLabelIds((current) => (
                  current.includes(label._id)
                    ? current.filter((id) => id !== label._id)
                    : [...current, label._id]
                ))}
              >
                {label.name}
              </ChipButton>
            ))}
            {!labels.length && (
              <Typography sx={{ color: PRES.textTertiary, fontFamily: TYPE.ui, ...TYPE.caption }}>
                {COPY.noLibraryLabels}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            disabled={!name.trim()}
            variant="contained"
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            onClick={async () => {
              try {
                const created = await createLibrarySlide({
                  name: name.trim(),
                  pageFormat,
                  labelIds: createLabelIds,
                });
                setCreateOpen(false);
                onOpen(created._id);
              } catch (err: any) {
                setError(err?.response?.data?.message || COPY.createFailed);
              }
            }}
          >
            {COPY.createAction}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(renameId)} onClose={() => setRenameId(null)} fullWidth maxWidth="xs">
        <DialogTitle>{COPY.renameLibrarySlide}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={COPY.librarySlideName}
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameId(null)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            disabled={!renameValue.trim() || !renameId}
            variant="contained"
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            onClick={async () => {
              if (!renameId) return;
              try {
                await patchLibrarySlide(renameId, { name: renameValue.trim() });
                setRenameId(null);
                await reload();
              } catch (err: any) {
                setError(err?.response?.data?.message || COPY.saveError);
              }
            }}
          >
            {COPY.confirm}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{COPY.manageLibraryLabels}</DialogTitle>
        <DialogContent>
          {!labels.length && (
            <Typography sx={{ mt: 1, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.body }}>
              {COPY.noLibraryLabels}
            </Typography>
          )}
          {labels.map((label) => {
            const draft = labelDrafts[label._id] ?? label.name;
            return (
              <Box key={label._id} sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  label={COPY.libraryLabelName}
                  value={draft}
                  onChange={(event) => setLabelDrafts((current) => ({ ...current, [label._id]: event.target.value }))}
                />
                <Button
                  disabled={!draft.trim() || draft.trim() === label.name}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                  onClick={async () => {
                    try {
                      await patchLibraryLabel(label._id, { name: draft.trim() });
                      await reload();
                    } catch (err: any) {
                      setError(err?.response?.data?.message || COPY.saveError);
                    }
                  }}
                >
                  {COPY.renameLibraryLabel}
                </Button>
                <Button
                  color="error"
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                  onClick={async () => {
                    if (!window.confirm(COPY.confirmDeleteLibraryLabel)) return;
                    try {
                      await deleteLibraryLabel(label._id);
                      if (labelFilter === label._id) setLabelFilter('');
                      await reload();
                    } catch (err: any) {
                      setError(err?.response?.data?.message || COPY.saveError);
                    }
                  }}
                >
                  {COPY.deleteLibraryLabel}
                </Button>
              </Box>
            );
          })}
          <Box sx={{ display: 'flex', gap: 1, mt: 3, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              label={COPY.newLibraryLabel}
              placeholder={COPY.libraryLabelNamePlaceholder}
              value={newLabelName}
              onChange={(event) => setNewLabelName(event.target.value)}
            />
            <Button
              disabled={!newLabelName.trim()}
              variant="contained"
              sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none', flexShrink: 0 }}
              onClick={async () => {
                try {
                  await createLibraryLabel(newLabelName.trim());
                  setNewLabelName('');
                  await reload();
                } catch (err: any) {
                  setError(err?.response?.data?.message || COPY.saveError);
                }
              }}
            >
              {COPY.createAction}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageOpen(false)} sx={{ textTransform: 'none' }}>{COPY.confirm}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LibraryHome;
